// src/components/HelpPage.jsx

import React, { useRef } from 'react';
import {
  AlertCircle,
  Archive,
  CheckCircle2,
  FileSpreadsheet,
  Inbox,
  ListChecks,
  MailCheck,
  PauseCircle,
  Send,
  Settings,
  ShieldCheck,
  UploadCloud
} from 'lucide-react';
import Sidebar from './SideBar';
import AbsoluteNavigation from './AbsoluteNavigation';
import '../styles/HelpPage.css';

const sectionLinks = [
  { key: 'overview', label: 'Overview' },
  { key: 'campaigns', label: 'Campaigns' },
  { key: 'actions', label: 'Actions' },
  { key: 'safety', label: 'Safety' },
  { key: 'troubleshooting', label: 'Troubleshooting' }
];

const quickStartSteps = [
  {
    icon: Settings,
    title: 'Connect your email',
    body: 'Use Connect mailbox on your dashboard to link your Microsoft mailbox before launching or replying from a campaign.'
  },
  {
    icon: Settings,
    title: 'Set your email signature',
    body: 'Set your email signature in Settings, then choose Professional, Custom, or None so outbound messages use your details before a campaign goes live.'
  },
  {
    icon: UploadCloud,
    title: 'Upload a spreadsheet',
    body: 'Use Manage Clients to upload your property list, map the columns, and name the campaign clearly.'
  },
  {
    icon: MailCheck,
    title: 'Review outbound messages',
    body: 'Check recipients, property names, and the first email body before the system starts sending.'
  },
  {
    icon: Inbox,
    title: 'Work from the dashboard',
    body: 'Use statuses, action cards, and conversation history to decide what needs your attention.'
  }
];

const statusRows = [
  ['Live', 'The campaign is running and SiteSift is monitoring replies.'],
  ['Needs Action', 'A broker reply needs a human decision, such as a tour response or new-property approval.'],
  ['Pending Send', 'A message is queued but has not been sent yet. Review or cancel it before it leaves.'],
  ['Stopped', 'Follow-ups and auto-replies are disabled for that conversation or campaign.'],
  ['Completed', 'Required information was collected or the campaign is finished.'],
  ['Archived', 'The campaign is hidden from active work but kept for history.']
];

const actionCards = [
  {
    icon: Send,
    title: 'Send Reply',
    body: 'Read the conversation, edit the recipient and body if needed, then send only when the message is ready to leave your mailbox.'
  },
  {
    icon: FileSpreadsheet,
    title: 'New Property',
    body: 'Approve a replacement property only if it belongs in the search. Dismiss it if it is not useful.'
  },
  {
    icon: PauseCircle,
    title: 'Tour Or Call',
    body: 'SiteSift pauses these conversations so you can choose the right human response instead of auto-sending.'
  },
  {
    icon: Archive,
    title: 'Stop Or Archive',
    body: 'Stop a conversation when it should not receive more follow-ups. Archive campaigns when they are no longer active work.'
  }
];

const sendChecks = [
  'Only send when the recipient, subject, and body look right.',
  'Confirm the property address in the thread matches the row you are handling.',
  'Remove placeholder text, blank bullets, and anything you would not personally send.',
  'Do not approve a new property unless it fits the client search.',
  'Use Stop when a conversation should no longer receive follow-ups.',
  'Refresh the dashboard if a row looks stale before taking another action.'
];

const troubleshootingRows = [
  ['Your mailbox is not connected', 'Use Connect mailbox on your dashboard to link your Microsoft mailbox again before sending or processing more replies.'],
  ['A campaign is missing', 'Check the Status filter. It may be Archived, Completed, or Stopped instead of Live.'],
  ['A broker reply needs judgment', 'Open the conversation, read the latest message, and use the action card instead of replying from memory.'],
  ['A sheet row looks wrong', 'Compare the dashboard conversation, Gmail thread, and spreadsheet row before editing the sheet manually.'],
  ['You are unsure whether to send', 'Do not send yet. Capture the campaign name, property address, and screenshot for your SiteSift admin.']
];

export default function HelpPage() {
  const sectionRefs = {
    overview: useRef(null),
    campaigns: useRef(null),
    actions: useRef(null),
    safety: useRef(null),
    troubleshooting: useRef(null)
  };

  const jumpToSection = (key) => {
    const target = sectionRefs[key]?.current;
    if (typeof target?.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content help-page">
        <AbsoluteNavigation />

        <header className="help-page__header">
          <div>
            <p className="help-page__eyebrow">SiteSift guide</p>
            <h2>Help Center</h2>
            <p className="help-page__subtitle">
              Use SiteSift to launch broker outreach, monitor replies, and keep your spreadsheet current while staying in control of what gets sent.
            </p>
          </div>
        </header>

        <nav className="help-page__tabs" aria-label="Help sections">
          {sectionLinks.map(section => (
            <button
              key={section.key}
              className="help-page__tab"
              type="button"
              onClick={() => jumpToSection(section.key)}
            >
              {section.label}
            </button>
          ))}
        </nav>

        <section
          className="help-section"
          id="overview"
          ref={sectionRefs.overview}
          aria-labelledby="overview-heading"
        >
          <div className="help-section__heading">
            <ListChecks size={20} />
            <h3 id="overview-heading">What To Do First</h3>
          </div>

          <div className="help-quick-grid">
            {quickStartSteps.map(step => {
              const Icon = step.icon;
              return (
                <article className="help-card" key={step.title}>
                  <div className="help-card__icon">
                    <Icon size={18} />
                  </div>
                  <h4>{step.title}</h4>
                  <p>{step.body}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section
          className="help-section"
          id="campaigns"
          ref={sectionRefs.campaigns}
          aria-labelledby="campaigns-heading"
        >
          <div className="help-section__heading">
            <UploadCloud size={20} />
            <h3 id="campaigns-heading">Start A Campaign</h3>
          </div>

          <div className="help-two-column">
            <div>
              <h4>Prepare the spreadsheet</h4>
              <ul className="help-list">
                <li>Use one row per property.</li>
                <li>Include broker email, property name or address, city, and the fields you want SiteSift to collect.</li>
                <li>Keep formula columns in the sheet if you need them, but do not map them as fields SiteSift should write.</li>
              </ul>
            </div>
            <div>
              <h4>Upload and review</h4>
              <ul className="help-list">
                <li>Open Manage Clients, upload the file, and confirm the column mapping.</li>
                <li>Name the campaign so you can recognize it later in the dashboard.</li>
                <li>Review the generated outreach before sending the first batch.</li>
              </ul>
            </div>
          </div>

          <div className="help-callout">
            <AlertCircle size={18} />
            <p>
              If a campaign has the wrong client name, wrong recipients, or confusing custom fields, pause before sending and fix the setup first.
            </p>
          </div>
        </section>

        <section className="help-section" aria-labelledby="statuses-heading">
          <div className="help-section__heading">
            <CheckCircle2 size={20} />
            <h3 id="statuses-heading">Understand Statuses</h3>
          </div>

          <div className="help-status-list">
            {statusRows.map(([label, description]) => (
              <div className="help-status-row" key={label}>
                <span>{label}</span>
                <p>{description}</p>
              </div>
            ))}
          </div>
        </section>

        <section
          className="help-section"
          id="actions"
          ref={sectionRefs.actions}
          aria-labelledby="actions-heading"
        >
          <div className="help-section__heading">
            <Inbox size={20} />
            <h3 id="actions-heading">Handle Dashboard Actions</h3>
          </div>

          <p className="help-section__intro">
            Actions mean the system found something that benefits from human judgment. Open the conversation, read the latest broker message, then choose the matching action.
          </p>

          <div className="help-action-grid">
            {actionCards.map(action => {
              const Icon = action.icon;
              return (
                <article className="help-action-card" key={action.title}>
                  <Icon size={18} />
                  <div>
                    <h4>{action.title}</h4>
                    <p>{action.body}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section
          className="help-section"
          id="safety"
          ref={sectionRefs.safety}
          aria-labelledby="safety-heading"
        >
          <div className="help-section__heading">
            <ShieldCheck size={20} />
            <h3 id="safety-heading">Before You Send</h3>
          </div>

          <div className="help-checklist" role="list">
            {sendChecks.map(check => (
              <div className="help-checklist__item" role="listitem" key={check}>
                <CheckCircle2 size={16} />
                <span>{check}</span>
              </div>
            ))}
          </div>
        </section>

        <section
          className="help-section"
          id="troubleshooting"
          ref={sectionRefs.troubleshooting}
          aria-labelledby="troubleshooting-heading"
        >
          <div className="help-section__heading">
            <AlertCircle size={20} />
            <h3 id="troubleshooting-heading">Troubleshooting</h3>
          </div>

          <div className="help-troubleshooting">
            {troubleshootingRows.map(([problem, fix]) => (
              <div className="help-troubleshooting__row" key={problem}>
                <h4>{problem}</h4>
                <p>{fix}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
